import calogo from './images/calogo.png';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, Linkedin, Mail, Download, Instagram,
  BookOpen, Target, Award, Users, ChevronLeft, ChevronRight, Briefcase,
  Camera, Book, Globe, Moon
} from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import portImage from './images/photo.png';
import resumePDF from './Siya_Khandelwal_Resume.pdf';

// --- Utility Functions ---
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// --- Shared Primitive Components ---
const Button = React.forwardRef(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? "div" : motion.button;
  return (
    <Comp
      ref={ref}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      className={cn(
        "inline-flex items-center justify-center rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#4a7ec8]/50 disabled:pointer-events-none disabled:opacity-50",
        "bg-[rgba(255,255,255,0.2)] text-[#1e3264] shadow-[0_8px_20px_rgba(30,50,180,0.3)] hover:shadow-[0_0_20px_rgba(90,100,200,0.4)] h-12 px-8 py-2 border border-[#4a7ec8] opacity-100",
        className
      )}
      {...props}
    />
  );
});
Button.displayName = "Button";

const Card = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-3xl border-[rgba(255,255,255,0.9)] border bg-white/[0.12] backdrop-blur-xl shadow-[0_8px_32px_rgba(60,100,160,0.25)]",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
Card.displayName = "Card";

// --- 3D Orb Component ---
const Orb = ({ className, color1, color2, delay = 0 }) => (
  <motion.div
    animate={{
      y: [0, -30, 0],
      x: [0, 20, 0],
      scale: [1, 1.05, 1],
      rotate: [0, 5, 0]
    }}
    transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay }}
    className={cn("absolute rounded-[40%] blur-[2px]", className)}
    style={{
      background: `radial-gradient(circle at 30% 30%, ${color1}, ${color2})`,
      boxShadow: `inset -20px -20px 60px rgba(0,0,0,0.1), inset 20px 20px 60px rgba(255,255,255,0.9)`
    }}
  />
);

// --- Page Sections ---

// --- Abstract Flows Component ---
const SilkRibbons = () => (
  <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
    <motion.div
      animate={{ x: [-8, 8, -8] }}
      transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      className="absolute inset-0"
    >
      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 1000 1000">
        {[...Array(10)].map((_, i) => (
          <path
            key={`r1-${i}`}
            d={`M-50,600 C200,-100 500,1100 750,-50`}
            fill="none"
            stroke="rgba(100,150,210,0.02)"
            strokeWidth="1"
            transform={`translate(${i * 8}, ${i * 6})`}
          />
        ))}
      </svg>
    </motion.div>
    <motion.div
      animate={{ x: [-8, 8, -8] }}
      transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      className="absolute inset-0"
    >
      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 1000 1000">
        {[...Array(8)].map((_, i) => (
          <path
            key={`r2-${i}`}
            d={`M-50,650 C250,-50 550,1150 800,-50`}
            fill="none"
            stroke="rgba(90,140,200,0.08)"
            strokeWidth="1.2"
            transform={`translate(${i * 7}, ${i * 8})`}
          />
        ))}
      </svg>
    </motion.div>
    <motion.div
      animate={{ x: [-8, 8, -8] }}
      transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 4 }}
      className="absolute inset-0"
    >
      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 1000 1000">
        {[...Array(12)].map((_, i) => (
          <path
            key={`r3-${i}`}
            d={`M-50,550 C150,-150 450,1050 700,-50`}
            fill="none"
            stroke="rgba(80,130,195,0.12)"
            strokeWidth="0.8"
            transform={`translate(${i * 9}, ${i * 7})`}
          />
        ))}
      </svg>
    </motion.div>
    <motion.div
      animate={{ x: [-8, 8, -8] }}
      transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 6 }}
      className="absolute inset-0"
    >
      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 1000 1000">
        {[...Array(10)].map((_, i) => (
          <path
            key={`r4-${i}`}
            d={`M-50,700 C300,0 600,1200 850,-50`}
            fill="none"
            stroke="rgba(70,120,190,0.18)"
            strokeWidth="1.5"
            transform={`translate(${i * 6}, ${i * 10})`}
          />
        ))}
      </svg>
    </motion.div>
    <motion.div
      animate={{ x: [-8, 8, -8] }}
      transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 8 }}
      className="absolute inset-0"
    >
      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 1000 1000">
        {[...Array(9)].map((_, i) => (
          <path
            key={`r5-${i}`}
            d={`M-50,625 C225,-75 525,1125 775,-50`}
            fill="none"
            stroke="rgba(58,109,181,0.22)"
            strokeWidth="1.2"
            transform={`translate(${i * 8}, ${i * 9})`}
          />
        ))}
      </svg>
    </motion.div>
  </div>
);




function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Education', href: '#education' },
    { name: 'Experience', href: '#experience' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[rgba(100,120,190,0.7)] backdrop-blur-xl shadow-[0_4px_30px_rgba(30,50,180,0.2)] py-4 border-b border-[rgba(255,255,255,0.9)]' : 'bg-transparent py-6'
          }`}
      >
        <div className="container mx-auto px-6 lg:px-12 flex justify-between items-center">
          <a href="#home" className="nav-left">
            <img src={calogo} alt="CA Logo" className="ca-logo" />
            <div className="logo-badge w-10 h-10 rounded-xl bg-[rgba(255,255,255,0.4)] flex items-center justify-center shadow-md border border-white/40">
              <span className="text-[#1e3264] text-sm font-bold font-heading">SK</span>
            </div>
            <span className="nav-name text-xl font-semibold text-[#1e3264] hidden md:block tracking-tight">Siya Khandelwal</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-8 bg-[rgba(255,255,255,0.70)] backdrop-blur-md px-8 py-3 rounded-full border border-[rgba(255,255,255,0.9)] shadow-sm">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm font-semibold text-[#2d3f5c] hover:text-gray-800 transition-colors relative group opacity-100"
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px]  opacity-100 transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
          </div>

          <div className="hidden md:block">
            <a href="#contact">
              <button className="bg-[#3a6db5] text-[#ffffff] font-semibold border-none opacity-100 px-[20px] py-[10px] rounded-[10px] hover:bg-[#1e3264] hover:shadow-[0_0_16px_rgba(58,109,181,0.35)] transition-all">Let's Connect</button>
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden text-[#3a4b6b] font-semibold opacity-100 p-2 focus:outline-none bg-[rgba(255,255,255,0.70)] rounded-full backdrop-blur-sm"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#ffffff]/95 backdrop-blur-2xl flex flex-col items-center justify-center space-y-8 lg:hidden"
          >
            <button
              className="absolute top-6 right-6 text-[#d0d5f5] p-2 focus:outline-none bg-[rgba(255,255,255,0.70)] rounded-full"
              onClick={() => setIsOpen(false)}
            >
              <X size={32} />
            </button>
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="text-3xl font-heading font-bold text-[#1e3264] hover:text-[#3a6db5] transition-colors"
              >
                {link.name}
              </a>
            ))}
            <a href="#contact" onClick={() => setIsOpen(false)}>
              <Button className="mt-4 shadow-xl">Contact Me</Button>
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Hero() {
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <section id="home" className="min-h-screen flex items-center pt-24 pb-12 relative z-10 w-full">
      <div className="container mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-4 items-center relative z-10">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-6 pt-10 relative z-[5]"
        >
          <motion.div variants={fadeUp}>
            <span className="inline-block py-1.5 px-4 rounded-full bg-[rgba(255,255,255,0.70)] border-[rgba(255,255,255,0.9)] border text-[#3a6db5] text-xs font-bold tracking-widest uppercase mb-6 opacity-100 shadow-sm backdrop-blur-md">
              CA Aspirant &middot; Mumbai
            </span>
            <h1 className="hero-heading text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-[#1a2d5a] tracking-[-0.01em] mb-4 tracking-tight opacity-100 text-transparent-none bg-transparent">
              Hi, I'm <br /><span className="text-[#1a2d5a] font-bold opacity-100">Siya Khandelwal</span>
            </h1>
            <h2 className="hero-subheading text-2xl md:text-3xl font-heading font-semibold text-[#2e426b] opacity-100 bg-transparent">
              Chartered Accountancy Student
            </h2>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-6 max-w-md bg-white/15 backdrop-blur-lg border border-white/30 rounded-2xl p-6 shadow-xl">
            <p className="hero-description text-[15px] text-[#1a2d5a] leading-relaxed font-medium">
              Cleared both groups of CA Intermediate in the first attempt. Seeking an Articleship opportunity at a reputed firm to leverage my strong analytical skills in Auditing and Financial Analysis.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-wrap gap-4 items-center pt-4">
            <a href="#education">
              <button className="hero-btn">My Qualifications</button>
            </a>
            <a href={resumePDF} download="Siya_Khandelwal_Resume.pdf" className="hero-btn">
              <Download size={20} />
              Download Resume
            </a>
          </motion.div>

          <motion.div variants={fadeUp} className="hero-socials flex gap-4 pt-6">
            <a href="https://www.linkedin.com/in/siyakhandelwal" target="_blank" rel="noreferrer" className="social-circle">
              <Linkedin size={20} strokeWidth={2.2} />
            </a>
            <a href="https://www.instagram.com/siyakhandelwall" target="_blank" rel="noreferrer" className="social-circle">
              <Instagram size={20} strokeWidth={2.2} />
            </a>
            <a href="mailto:siya.khandelwal089@gmail.com" className="social-circle">
              <Mail size={20} strokeWidth={2.2} />
            </a>
          </motion.div>
        </motion.div>

        {/* Hero Right Visuals - 3D Presentation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative flex justify-center w-full mt-10 lg:mt-0"
        >
          {/* Main Display Glass */}
          <div className="relative w-full max-w-lg aspect-square lg:aspect-[4/5] rounded-[18px] border-[2px] border-[rgba(58,109,181,0.15)] bg-[#ffffff] backdrop-blur-[24px] p-2 shadow-[0_18px_40px_rgba(0,0,0,0.15)] flex items-center justify-center">

            {/* Inner frame */}
            <div className="relative w-full h-full bg-[#ffffff] rounded-[18px] overflow-hidden p-6 flex flex-col justify-end shadow-inner">
              {/* Image */}
              <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="w-full h-full rounded-[1.5rem] bg-[#f0f4f8]/30 shadow-inner overflow-hidden relative">
                  <img src={portImage} alt="Siya Khandelwal" className="w-full h-full object-cover rounded-[14px] mix-blend-normal opacity-100 transition-transform duration-700 hover:scale-105" />
                </div>
              </div>

              {/* Decorative Bottom Bar inside glass */}
              <div className="relative z-10 w-full h-16 bg-[rgba(255,255,255,0.70)] backdrop-blur-lg rounded-2xl border border-[rgba(255,255,255,0.9)] flex items-center px-6 justify-between shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#ffffff]" />
                  <div className="w-3 h-3 rounded-full bg-[#4a7ec8]" />
                  <div className="w-3 h-3 rounded-full bg-[#3a6db5]" />
                </div>
                <div className="text-[#d0d5f5] font-bold text-sm tracking-widest">PORTFOLIO</div>
              </div>
            </div>

            {/* Floating UI Badges */}
            <motion.div
              animate={{ y: [-15, 15] }}
              transition={{ duration: 5, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
              className="absolute -top-8 -right-8 w-20 h-20 rounded-2xl bg-[rgba(255,255,255,0.70)] backdrop-blur-[30px] border-[rgba(255,255,255,0.9)] border flex items-center justify-center shadow-[0_8px_32px_rgba(60,100,160,0.25)] rotate-12"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3a6db5] to-[#4a7ec8] opacity-100 flex items-center justify-center shadow-inner">
                <span className="text-[#1e3264] opacity-100 font-bold text-2xl">%</span>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [15, -15] }}
              transition={{ duration: 4.5, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: 1 }}
              className="absolute -bottom-6 left-12 w-16 h-16 rounded-full bg-[rgba(255,255,255,0.70)] backdrop-blur-[30px] border-[rgba(255,255,255,0.9)] border flex items-center justify-center shadow-[0_8px_32px_rgba(60,100,160,0.25)]"
            >
              <span className="text-[#1e3264] opacity-100 font-bold text-2xl">₹</span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function About() {
  const skills = [
    { icon: '📊', name: 'Excel / Tally' },
    { icon: '📝', name: 'Auditing Standards' },
    { icon: '⚖️', name: 'Taxation Laws' },
    { icon: '🗣️', name: 'Communication' },
    { icon: '👑', name: 'Leadership' },
  ];

  return (
    <section id="about" className="py-24 relative z-10 bg-[#e8eef5]">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <Card className="max-w-5xl mx-auto p-8 md:p-14 relative overflow-hidden">
            <div className="flex flex-col md:flex-row gap-12 items-center relative z-10">

              <div className="w-full md:w-1/3 flex justify-center">
                <div className="relative w-56 h-56 sm:w-64 sm:h-64">
                  <div className="absolute inset-0 bg-[rgba(255,255,255,0.70)] rounded-full blur-2xl animate-pulse" />
                  <div className="absolute inset-2 rounded-full bg-gradient-to-br from-white/80 to-white/20 border-4 border-white shadow-[0_0_30px_rgba(30,50,180,0.3)] overflow-hidden p-1">
                    <img src={portImage} alt="Siya Khandelwal" className="w-full h-full object-cover rounded-full mix-blend-normal" />
                  </div>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
                    className="absolute -inset-6 border-[3px] border-dashed border-[#3a6db5]/60 rounded-full"
                  />
                </div>
              </div>

              <div className="w-full md:w-2/3 space-y-6">
                <h2 className="text-4xl lg:text-5xl font-heading font-extrabold text-[#1e3264] flex items-center gap-4">
                  About me
                  <span className="inline-block flex-1 max-w-[120px] h-[3px] bg-gradient-to-r from-[#3a6db5] to-transparent rounded-full" />
                </h2>
                <div className="space-y-4 text-[#1e3264]/80 leading-relaxed font-medium text-base lg:text-lg mb-6">
                  <p>
                    I am <strong className="text-[#3a6db5] font-bold">Siya Khandelwal</strong>, a highly motivated Chartered Accountancy student. With a solid academic foundation and the successful clearance of CA Intermediate (Both Groups) in my first attempt, I am eager to transition my academic knowledge into real-world applications.
                  </p>
                  <p>
                    My core strengths lie in financial analysis, auditing standards, and an unwavering attention to detail. Beyond academics, I have developed strong leadership and communication skills through my tenure as a Group Leader in the National Service Scheme.
                  </p>
                </div>

                <div className="pt-4">
                  <h3 className="section-subtitle text-sm tracking-widest mb-4 uppercase">Core Strengths</h3>
                  <div className="flex flex-wrap gap-3">
                    {skills.map((skill, idx) => (
                      <span key={idx} className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-[rgba(255,255,255,0.70)] border border-[rgba(255,255,255,0.9)] text-[#1e3264] font-bold hover:bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.3)] hover:border-[#3a6db5] hover:text-[#3a6db5] hover:shadow-[0_4px_15px_rgba(91,200,216,0.15)] transition-all transform hover:-translate-y-1 backdrop-blur-md">
                        <span className="text-lg">{skill.icon}</span> {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}

function SkillsExpertise() {
  const services = [
    {
      title: 'Technical Skills',
      desc: 'Proficiency in MS Word, MS Excel, MS PowerPoint, Tally, and Zoho Books.',
      icon: BookOpen,
      color: "from-[#3a6db5] to-[#4a7ec8] opacity-100"
    },
    {
      title: 'Audit & Taxation',
      desc: 'Deep knowledge of Accounting Standards, Auditing procedures, and Taxation Laws.',
      icon: Target,
      color: "from-[#3a6db5] to-[#4a7ec8] opacity-100"
    },
    {
      title: 'Soft Skills',
      desc: 'Strong communication, work ethic, problem-solving, and attention to detail.',
      icon: Users,
      color: "from-[#4a7ec8] to-[#4a7ec8]"
    },
    {
      title: 'Certifications',
      desc: 'Certified in Leadership Skills by IIMA (Coursera) with an 83.25% grade.',
      icon: Award,
      color: "from-[#4a7ec8] to-[#3a6db5]"
    },
  ];

  return (
    <section id="skills" className="py-24 relative z-10 w-full bg-[#f0f4f8]">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-heading font-bold text-[#1e3264] tracking-[-0.01em] opacity-100 mb-4">Skills & Expertise</h2>
          <p className="section-subtitle max-w-xl mx-auto text-lg">
            A comprehensive overview of my technical capabilities and professional strengths.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <Card
                className="group h-full p-8 hover:scale-[1.03] hover:bg-[rgba(255,255,255,0.70)] hover:border-[rgba(255,255,255,0.9)] hover:shadow-[0_8px_28px_rgba(58,109,181,0.12)] transition-all duration-300 relative overflow-hidden flex flex-col items-center text-center cursor-pointer"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                <div className={cn("w-20 h-20 rounded-[1.5rem] bg-[rgba(255,255,255,0.12)] flex items-center justify-center mb-6 text-[#1e3264] opacity-100 shadow-lg group-hover:shadow-[0_0_20px_rgba(91,200,216,0.4)] transition-all duration-300 transform group-hover:-translate-y-2", service.color)}>
                  <service.icon size={32} strokeWidth={2} />
                </div>

                <h3 className="text-xl font-heading font-bold text-[#1e3264] mb-3">{service.title}</h3>
                <p className="text-[#1e3264]/70 font-medium leading-relaxed">
                  {service.desc}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EducationAchievements() {
  const projects = [
    {
      title: "CA Intermediate (Group I & II)",
      category: "ICAI | Jan 2026",
      desc: "Cleared Both Groups in First Attempt. Exemption secured in Auditing & Ethics and Financial Management & Strategic Management.",
      score: "52.67% (G1) | 60% (G2)",
      highlight: true
    },
    {
      title: "CA Foundation",
      category: "ICAI | Jan 2025",
      desc: "Cleared CA Foundation examinations with Distinction.",
      score: "71.75% (287/400)",
    },
    {
      title: "B.Com (Management & Finance)",
      category: "Mithibai College | 2024-2027",
      desc: "SVKM'S Mithibai College of Arts, Chauhan Institute of Science & Commerce, Mumbai.",
      score: "GPA: 9.18 (Sem III)",
    },
    {
      title: "Class XII (CBSE)",
      category: "High School | 2024",
      desc: "Roop Nagar Public School, Jhalawar, Rajasthan.",
      score: "Secured 90.20%",
    },
    {
      title: "Class X (CBSE)",
      category: "High School | 2022",
      desc: "Disha Delphi Public School (By ALLEN), Kota, Rajasthan.",
      score: "Secured 95.00%",
    },
    {
      title: "IT & Soft Skills Training",
      category: "ICAI | 2026",
      desc: "Completed Orientation Course (OC) and Information Technology (IT) course. Acted as Class Representative for both.",
      score: "Disciplined & Responsive",
    }
  ];

  return (
    <section id="education" className="py-24 relative z-10 w-full rounded-[4rem] bg-[rgba(255,255,255,0.70)] border-y border-[rgba(255,255,255,0.9)] backdrop-blur-sm shadow-[inset_0_20px_60px_rgba(255,255,255,0.9)] bg-[#e8eef5]">
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-heading font-bold text-[#1e3264] tracking-[-0.01em] opacity-100 mb-4">Education & Achievements</h2>
          <p className="section-subtitle max-w-xl mx-auto text-lg">
            My academic journey and professional qualifications.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <Card className={cn("group overflow-hidden rounded-[2.5rem] p-8 border-white/15 bg-[rgba(255,255,255,0.70)] bg-[rgba(255,255,255,0.70)] transition-all duration-500 hover:shadow-[0_8px_28px_rgba(58,109,181,0.12)] hover:scale-[1.02] h-full flex flex-col hover:bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.3)]", project.highlight && "border-[#3a6db5]/50 bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.3)]")}>
                <div className="flex flex-col flex-1 relative z-10">
                  <div className="flex flex-col mb-4 gap-2">
                    <span className="self-start text-[11px] uppercase tracking-[0.15em] font-black py-1.5 px-4 rounded-full bg-[rgba(255,255,255,0.12)] border border-white/30 text-[#1e3264] opacity-100 shadow-sm">
                      {project.category}
                    </span>
                    <h3 className="text-2xl font-heading font-semibold text-gray-800 leading-tight mt-2">{project.title}</h3>
                  </div>
                  <p className="text-base text-[#2d3f5c] font-medium leading-relaxed mb-6 flex-1">
                    {project.desc}
                  </p>
                  <div className="pt-4 border-t-2 border-[rgba(255,255,255,0.9)] flex items-center gap-3">
                    <Target size={20} className="text-[#b8c0e8] opacity-100" />
                    <span className="text-base font-medium text-[#1e3264] opacity-100 font-heading tracking-wide">
                      {project.score}
                    </span>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Experience() {
  const experiences = [
    {
      text: "Served as a Group Leader and led a team of 20+ students, fostering teamwork and achieving collective goals.",
      name: "National Service Scheme",
      role: "January 2025",
    },
    {
      text: "Maintained an active, consistent volunteer presence providing continued support beyond my formal leadership term.",
      name: "National Service Scheme",
      role: "January 2025",
    },
    {
      text: "Established a consistent track record of social responsibility through community initiatives such as Blood Donation Campaigns and Distribution Drives.",
      name: "National Service Scheme",
      role: "January 2025",
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % experiences.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? experiences.length - 1 : prev - 1));
  };

  return (
    <section id="experience" className="py-24 relative z-10 overflow-hidden w-full bg-[#f0f4f8]">
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-heading font-bold text-[#1e3264] tracking-[-0.01em] opacity-100 mb-4">Leadership Experience</h2>
          <p className="section-subtitle max-w-xl mx-auto text-lg">
            Highlighting my commitment to social responsibility and team leadership.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto relative select-none">
          <div className="relative overflow-hidden px-4 md:px-16 py-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50, filter: "blur(8px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, x: -50, filter: "blur(8px)" }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <Card className="p-8 md:p-14 text-center border-[rgba(100,150,200,0.15)] border bg-[rgba(255,255,255,0.70)] rounded-[3rem] shadow-[0_20px_50px_rgba(30,50,180,0.2)] relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 text-[10rem] text-[#4a7ec8]/90 drop-shadow-md rotate-12 pointer-events-none">
                    "
                  </div>
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#3a6db5] to-[#4a7ec8] opacity-100 rounded-2xl flex items-center justify-center text-gray-800 shadow-xl mb-8 transform -rotate-6">
                    <Briefcase size={40} className="text-[#1e3264] fill-[#1e3264] opacity-100" />
                  </div>
                  <p className="text-xl md:text-2xl text-[#1e3264] opacity-100 leading-relaxed mb-10 max-w-2xl mx-auto font-semibold">
                    {experiences[currentIndex].text}
                  </p>
                  <div className="flex flex-col items-center gap-3 relative z-10">
                    <div>
                      <h4 className="text-xl text-[#1e3264] font-semibold opacity-100 tracking-wide font-heading">{experiences[currentIndex].name}</h4>
                      <p className="text-sm text-[#d0d5f5] font-bold uppercase tracking-widest mt-1 bg-[rgba(255,255,255,0.70)] inline-block px-4 py-1 rounded-full">{experiences[currentIndex].role}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </AnimatePresence>
          </div>

          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/25 backdrop-blur-md border border-[rgba(255,255,255,0.85)] flex items-center justify-center text-[#1e3264] opacity-100 hover:bg-[#3a6db5] hover:text-gray-800 hover:border-[#3a6db5] hover:scale-110 transition-all z-20 shadow-[0_8px_20px_rgba(30,50,180,0.3)]"
          >
            <ChevronLeft size={28} strokeWidth={2.5} />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/25 backdrop-blur-md border border-[rgba(255,255,255,0.85)] flex items-center justify-center text-[#1e3264] opacity-100 hover:bg-[#3a6db5] hover:text-gray-800 hover:border-[#3a6db5] hover:scale-110 transition-all z-20 shadow-[0_8px_20px_rgba(30,50,180,0.3)]"
          >
            <ChevronRight size={28} strokeWidth={2.5} />
          </button>

          <div className="flex justify-center gap-3 mt-6">
            {experiences.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-3 rounded-full transition-all duration-300 shadow-inner ${idx === currentIndex ? 'bg-[#f0f4f8] w-10 opacity-100' : 'bg-[rgba(255,255,255,0.5)] border border-[rgba(255,255,255,0.9)] w-3 hover:bg-white'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Hobbies() {
  const hobbies = [
    { icon: <Camera size={28} strokeWidth={2} />, name: "Content Creation", desc: "Creating reels & videos", link: "https://www.instagram.com/siyakhandelwall?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" },
    { icon: <Book size={28} strokeWidth={2} />, name: "Reading", desc: "Books & financial journals", link: null },
    { icon: <Globe size={28} strokeWidth={2} />, name: "Exploring", desc: "Discovering new places", link: null },
    { icon: <Moon size={28} strokeWidth={2} />, name: "Sleeping", desc: "Recharging like a pro", link: null }
  ];

  return (
    <section id="hobbies" className="py-24 relative z-10 w-full bg-[#e8eef5]">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-heading font-bold text-[#1e3264] tracking-[-0.01em] opacity-100 mb-4">Beyond the Numbers</h2>
          <p className="section-subtitle max-w-xl mx-auto text-lg">
            When I'm not studying or analyzing financials, here's what I am up to.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {hobbies.map((hobby, idx) => {
            const InnerContent = (
              <Card className="group h-full p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(30,50,180,0.3)] hover:border-[rgba(100,150,200,0.15)] hover:bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.3)] bg-white/[0.12] border-[rgba(100,150,200,0.15)] flex flex-col items-center text-center cursor-pointer relative overflow-hidden">
                <div className="w-16 h-16 rounded-2xl bg-white/[0.25] border border-[rgba(255,255,255,0.9)] flex items-center justify-center text-[#1e3264] opacity-100 stroke-[#1e3264] shadow-md group-hover:scale-110 group-hover:bg-[#3a6db5] group-hover:text-[#1e3264] group-hover:border-[#3a6db5] transition-all duration-500 mb-6">
                  {hobby.icon}
                </div>
                <h3 className="text-xl font-heading font-bold text-[#1e3264] opacity-100 mb-2">{hobby.name}</h3>
                <p className="text-[#2d3f5c] opacity-100 font-medium text-sm">
                  {hobby.desc}
                </p>
              </Card>
            );

            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="h-full"
              >
                {hobby.link ? (
                  <a href={hobby.link} target="_blank" rel="noopener noreferrer" className="block h-full outline-none">
                    {InnerContent}
                  </a>
                ) : (
                  InnerContent
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [formDone, setFormDone] = useState(false);
  const [firm, setFirm] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const handleForm = (e) => {
    e.preventDefault();
    const subject = `Hey, I have a query — from ${firm}`;
    const body = `Company Name: ${firm}\n Mail us: ${email}\n\nMessage:\n${message}`;
    window.open(
      `mailto:siya.khandelwal089@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      '_blank'
    );
    setFormDone(true);
    setTimeout(() => setFormDone(false), 4000);
    setFirm('');
    setEmail('');
    setMessage('');
  };

  return (
    <section id="contact" className="py-24 relative z-10 w-full bg-[#f0f4f8]">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <Card className="max-w-6xl mx-auto p-0 overflow-hidden bg-[rgba(255,255,255,0.70)] rounded-[3rem] border-[rgba(100,150,200,0.15)] border">
            <div className="grid lg:grid-cols-2">

              <div className="p-10 md:p-16 relative flex flex-col justify-center overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/40 to-transparent pointer-events-none" />

                <div className="relative z-10">
                  <h2 className="text-4xl md:text-5xl font-heading font-bold text-[#1e3264] tracking-[-0.01em] opacity-100 mb-6 leading-tight">
                    Let's Connect & <br /><span className="text-[#1a2d5a] ">Collaborate</span>
                  </h2>
                  <p className="text-[#2d3f5c] font-medium text-lg mb-10 max-w-md opacity-100">
                    Looking for a dedicated and skilled CA Article to join your firm? Let's discuss how I can contribute.
                  </p>

                  <div className="space-y-6 mb-8">
                    <div className="flex items-center gap-4 text-[#1e3264] font-bold">
                      <div className="w-14 h-14 rounded-2xl bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.3)] border border-[rgba(255,255,255,0.9)] flex items-center justify-center text-[#1e3264] opacity-100 shadow-sm backdrop-blur-md">
                        <Mail size={24} strokeWidth={2.5} />
                      </div>
                      <span className="text-base tracking-wide">siya.khandelwal089@gmail.com</span>
                    </div>
                    <div className="flex items-center gap-4 text-[#1e3264] font-bold">
                      <div className="w-14 h-14 rounded-2xl bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.3)] border border-[rgba(255,255,255,0.9)] flex items-center justify-center text-[#1e3264] opacity-100 shadow-sm backdrop-blur-md">
                        <span className="text-2xl">☎</span>
                      </div>
                      <span className="text-base tracking-wide">+91 90797 30036</span>
                    </div>
                    <div className="flex items-center gap-4 text-[#1e3264] font-bold">
                      <div className="w-14 h-14 rounded-2xl bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.3)] border border-[rgba(255,255,255,0.9)] flex items-center justify-center text-[#1e3264] opacity-100 shadow-sm backdrop-blur-md">
                        <span className="text-2xl">📍</span>
                      </div>
                      <span className="text-base tracking-wide">Mumbai, MH / Kota, RJ</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-10 md:p-16 bg-[rgba(100,120,190,0.7)] backdrop-blur-xl border-l-2 border-[rgba(255,255,255,0.9)] shadow-[inset_0_0_50px_rgba(255,255,255,0.9)]">
                <form onSubmit={handleForm} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-xs font-black tracking-widest uppercase text-[#b8c0e8] block opacity-100">Firm / Organization</label>
                    <input
                      type="text"
                      required
                      value={firm}
                      onChange={(e) => setFirm(e.target.value)}
                      placeholder="Organization Name"
                      className="w-full bg-[rgba(255,255,255,0.70)] border-[rgba(255,255,255,0.9)] border rounded-2xl px-5 py-4 text-[#1e3264] font-bold placeholder:text-[#1e3264]/90 focus:outline-none focus:border-[#3a6db5] focus:ring-4 focus:ring-[#3a6db5]/20 transition-all shadow-inner backdrop-blur-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black tracking-widest uppercase text-[#b8c0e8] block opacity-100">Email</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="contact@firm.com"
                      className="w-full bg-[rgba(255,255,255,0.70)] border-[rgba(255,255,255,0.9)] border rounded-2xl px-5 py-4 text-[#1e3264] font-bold placeholder:text-[#1e3264]/90 focus:outline-none focus:border-[#3a6db5] focus:ring-4 focus:ring-[#3a6db5]/20 transition-all shadow-inner backdrop-blur-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black tracking-widest uppercase text-[#b8c0e8] block opacity-100">Message</label>
                    <textarea
                      required
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="We would like to invite you for an interview..."
                      className="w-full bg-[rgba(255,255,255,0.70)] border-[rgba(255,255,255,0.9)] border rounded-2xl px-5 py-4 text-[#1e3264] font-bold placeholder:text-[#1e3264]/90 focus:outline-none focus:border-[#3a6db5] focus:ring-4 focus:ring-[#3a6db5]/20 transition-all resize-none shadow-inner backdrop-blur-sm"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center justify-center w-full py-4 text-base font-bold text-gray-800 shadow-[0_8px_20px_rgba(30,50,180,0.3)] hover:shadow-[0_12px_25px_rgba(91,200,216,0.5)] bg-[#3a6db5] text-[#1e3264] font-semibold rounded-full border border-[rgba(255,255,255,0.9)] transition-all cursor-pointer opacity-100"
                  >
                    {formDone ? "Query Drafted!" : "Send Message"}
                  </motion.button>
                </form>
              </div>

            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-8 border-t border-[rgba(58,109,181,0.20)] relative z-10 w-full bg-[#e8eef5]">
      <div className="container mx-auto px-6 lg:px-12 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#3a6db5] border border-[#3a6db5] text-[#ffffff] text-sm font-heading font-bold shadow-md opacity-100">SK</div>
          <span className="text-base font-semibold text-[#1e3264] opacity-100 tracking-widest uppercase">Siya Khandelwal</span>
        </div>
        <div className="flex gap-4">
          <a href="https://www.instagram.com/siyakhandelwall" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-[rgba(255,255,255,0.80)] border border-[#3a6db5] flex items-center justify-center group hover:bg-[#3a6db5] transition-all">
            <Instagram size={18} className="text-[#3a6db5] opacity-100 group-hover:text-[#ffffff]" />
          </a>
        </div>
        <p className="text-sm text-[#4a6080] font-bold tracking-wide opacity-100">
          © {new Date().getFullYear()} All rights reserved. CA Aspirant.
        </p>
      </div>
    </footer>
  );
}

// --- Main App Registration ---

export default function App() {
  return (
    <div className="relative w-full min-h-screen font-body bg-gradient-to-br from-[#f5f9ff] via-[#1a2d5a] to-[#f5f9ff] overflow-hidden selection:bg-[#3a6db5] selection:text-gray-800">

      {/* Global 3D Floating Orbs */}
      <SilkRibbons />

      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">

        <Orb className="w-[50vw] h-[50vw] top-[-10%] left-[-10%] opacity-35 mix-blend-multiply" color1="#f5f9ff" color2="#1a2d5a" delay={0} />
        <Orb className="w-[45vw] h-[45vw] top-[30%] right-[-15%] opacity-30 mix-blend-multiply" color1="#3a6db5" color2="#f5f9ff" delay={2} />
        <Orb className="w-[60vw] h-[60vw] bottom-[-20%] left-[10%] opacity-25 mix-blend-multiply" color1="#4a7ec8" color2="#3a6db5" delay={4} />
      </div>

      <Navbar />
      <Hero />
      <About />
      <SkillsExpertise />
      <EducationAchievements />
      <Experience />
      <Hobbies />
      <Contact />
      <Footer />
    </div>
  );
}
